// Card 34, limit lines (R548; ui-map-interface.md §17.3.34).
//
// A paper chart draws a dotted line where a thing stops — the limit of drift
// ice — and letters the line itself. This piece draws the three limits this
// map has:
//
//   1. a settled Beacon's beam, at `anchorRings` cell pitches, lettered
//      along the bottom arc so it reads left to right;
//   2. a sonar site's own listening circle, when the pointer is over the
//      site (`scene.ringsPx` is what the SERVER says this commander hears,
//      so the piece invents no reach of its own, R112);
//   3. the reach a sharing Oracle lends the squad while it broadcasts
//      (R453), which is not that hull's circle on any other frame.
//
// Gold, because the anchor ring of §6 already owns that hue for a Beacon.
//
// **HOVER OR SELECTION ONLY.** The owner ruled the piece out of the resting
// map: a limit for every Beacon at once is a map of circles. The TOML `show`
// carries the ruling — `"hover"` draws the one thing the pointer or the
// selection names, `"always"` draws one per settled Beacon — so the owner
// can try the other reading without a code change.

import { CHART } from '../../../gen/presentation';
import { chart as S } from '../../../gen/strings';
import { polylineLength } from '../../../render/text-run';
import { COL_PITCH, cellCenter } from '../../../world';
import type { FrameInfo } from '../../layer';
import type {
  ChartContext, ChartPack, ChartSinks, ChartTier, Piece, RebuildReason,
} from '../context';
import {
  anchorRingsOf, namedBeacon, namedFleet, namedSite, settledBeacons, sharingOracles,
} from './beacon-read';
import { CHART_INK, dashedRing, faceOf, showsAlways } from './shared';

/** Where the lettered arc starts and ends, in radians (the card's figures). */
const ARC_FROM = 2.7;
const ARC_TO = 1.3;

/** How many straight pieces the lettered arc is sampled into. */
const ARC_STEPS = 24;

/** Does the shipped table draw every settled Beacon, or only the named one? */
const LIMIT_ALWAYS = showsAlways(CHART.pieces.limit_line.show);

/** One circle the piece draws, and the words it carries. */
interface Limit {
  x: number;
  y: number;
  r: number;
  /** Is this a Beacon's beam? Only a beam is lettered. */
  lettered: boolean;
  /** The ring count the modern legend states. */
  rings: number;
}

export class LimitLinePiece implements Piece {
  readonly id = 'limit_line' as const;
  readonly tier: ChartTier = 'screen';
  readonly pack: ChartPack = 'static';
  readonly film = 'hide' as const;
  readonly rebuildOn: readonly RebuildReason[];

  private limits: Limit[] = [];

  /**
   * `always` takes the piece off the pointer, which is the reading the
   * owner may still ask for. The flag is a parameter so the test can state
   * both ways; the shipped registry passes nothing and takes the table's.
   */
  constructor(private readonly always: boolean = LIMIT_ALWAYS) {
    this.rebuildOn = always
      ? ['hover', 'selection', 'state', 'reading']
      : ['hover', 'selection', 'reading'];
  }

  /** How many circles stand now. The piece's test reads it. */
  get count(): number { return this.limits.length; }

  /**
   * The piece's own gate on top of the common one.
   *
   * `show = "always"` needs no pointer. `show = "hover"` waits for the
   * pointer or the selection to name a hull or a site, which is the cheap
   * test: whether that hull is a Beacon is the rebuild's question.
   */
  visible(ctx: ChartContext): boolean {
    return this.always || namedFleet(ctx) !== 0 || namedSite(ctx) !== 0;
  }

  rebuild(ctx: ChartContext): void {
    this.limits = [];
    const rings = anchorRingsOf(ctx);
    const beamR = rings * COL_PITCH;
    if (this.always) {
      for (const b of settledBeacons(ctx.scene)) {
        this.limits.push({ x: b.x, y: b.y, r: beamR, lettered: true, rings });
      }
      for (const o of sharingOracles(ctx.scene)) {
        this.limits.push({ x: o.x, y: o.y, r: o.sonarPx, lettered: false, rings });
      }
      return;
    }
    const beacon = namedBeacon(ctx);
    if (beacon) {
      this.limits.push({ x: beacon.x, y: beacon.y, r: beamR, lettered: true, rings });
    }
    const named = namedFleet(ctx);
    const oracle = sharingOracles(ctx.scene).find((o) => o.id === named);
    if (oracle) {
      this.limits.push({ x: oracle.x, y: oracle.y, r: oracle.sonarPx, lettered: false, rings });
    }
    const site = siteRing(ctx);
    if (site) this.limits.push({ ...site, lettered: false, rings });
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    if (this.limits.length === 0) return;
    const t = ctx.cfg.pieces.limit_line;
    const reading = ctx.reading;
    const r = t[reading];
    const face = faceOf(reading);
    const gold = CHART_INK.gold;
    for (const lim of this.limits) {
      dashedRing(sinks.circles, lim.x, lim.y, lim.r, t.fig.width, r.dash, gold, t.fig.alpha);
      if (!lim.lettered) continue;
      const pts = bottomArc(lim.x, lim.y, lim.r + t.fig.text_offset);
      const text = reading === 'modern'
        ? S.limit_modern({ rings: lim.rings })
        : S.limit_historical;
      ctx.glyphs.wordAlong(
        sinks.quads, text, pts,
        face, t.text.main, gold, r.alpha,
        { anchor: 'centre', startS: polylineLength(pts) / 2, letterSpacing: r.fig.spacing },
      );
    }
  }
}

/**
 * The listening circle of the site under the pointer, or null.
 *
 * The client decides nothing about who hears through what: it looks the
 * site's own place up in the ring list the server sent, and a site that
 * grants this commander nothing lights nothing.
 */
function siteRing(ctx: ChartContext): { x: number; y: number; r: number } | null {
  const id = namedSite(ctx);
  if (id === 0) return null;
  const site = ctx.scene.sites?.get(id);
  if (!site) return null;
  const [x, y] = cellCenter(site.cell);
  for (const ring of ctx.scene.ringsPx) {
    const dx = ring.x - x, dy = ring.y - y;
    if (dx * dx + dy * dy < 1) return { x, y, r: ring.range };
  }
  return null;
}

/**
 * The bottom arc of a circle, as a polyline, from `ARC_FROM` round to
 * `ARC_TO`.
 *
 * The card runs the lettering from 2.7 rad down to 1.3, which crosses the
 * lowest point of the circle, so the run reads left to right on the screen.
 */
function bottomArc(cx: number, cy: number, r: number): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  for (let i = 0; i <= ARC_STEPS; i++) {
    const a = ARC_FROM + ((ARC_TO - ARC_FROM) * i) / ARC_STEPS;
    out.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
  }
  return out;
}
